import { useId } from 'react';
import { fromGramsMilligrams, toGramsMilligrams } from '@vj/shared';

/**
 * Grams + milligrams, entered as two boxes exactly as specified, collapsed to
 * one canonical decimal value for the parent to hold — the same shape the
 * database and the GST engine expect, so no conversion happens anywhere else
 * in the form.
 *
 * @param {string|number} value grams, as a decimal (e.g. "10.500")
 * @param {(next: string) => void} onChange
 */
export function WeightInput({ label, value, onChange, error, required }) {
  const id = useId();
  const { grams, milligrams } = toGramsMilligrams(value || 0);

  const update = (nextGrams, nextMg) => {
    onChange(fromGramsMilligrams(nextGrams || 0, nextMg || 0).toString());
  };

  return (
    <div className="flex flex-col gap-1.5">
      {label ? (
        <label htmlFor={`${id}-g`} className="text-sm font-medium text-content">
          {label}
          {required ? <span className="text-danger"> *</span> : null}
        </label>
      ) : null}
      <div className="flex items-stretch gap-1.5">
        <div className="relative flex-1">
          <input
            id={`${id}-g`}
            type="number"
            inputMode="decimal"
            min="0"
            step="1"
            value={grams || ''}
            onChange={(e) => update(Number(e.target.value), milligrams)}
            className="h-touch w-full rounded-card border border-border bg-surface pl-3 pr-7 text-content"
            aria-label={`${label ?? 'Weight'} — grams`}
          />
          <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-muted">g</span>
        </div>
        <div className="relative w-24">
          <input
            id={`${id}-mg`}
            type="number"
            inputMode="numeric"
            min="0"
            max="999"
            step="1"
            value={milligrams || ''}
            onChange={(e) => update(grams, Number(e.target.value))}
            className="h-touch w-full rounded-card border border-border bg-surface pl-3 pr-8 text-content"
            aria-label={`${label ?? 'Weight'} — milligrams`}
          />
          <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-muted">mg</span>
        </div>
      </div>
      {error ? <p className="text-sm text-danger">{error}</p> : null}
    </div>
  );
}

export default WeightInput;
